import type React from 'react';

import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { EditorState, Transaction, Selection } from '@atlaskit/editor-prosemirror/state';
import { Decoration, DecorationSet } from '@atlaskit/editor-prosemirror/view';
import type { ConflictChange } from '@atlaskit/editor-common/collab';
import type { Mapping } from '@atlaskit/editor-prosemirror/transform';
import { token } from '@atlaskit/tokens';
import { convertToInlineCss } from '@atlaskit/editor-common/css-helper';

import { getNavigationInfo, getNewConflicts, type Metadata } from './pluginState.ts';
import { conflictResolutionPluginKey } from './pluginKey.ts';
import { createDeletedDecoration } from './deletedDecoration.tsx';
import { createBlockNodeConflictDecoration } from './blockNodeConflictDecoration.ts';

export type ResolutionMode = 'viewing' | 'resolving';

export type IdentifiableConflictChange = ConflictChange & { id: string };

export type ConflictChangeWithType = IdentifiableConflictChange & { type: 'deleted' | 'inserted' };

export type Conflicts = {
	inserted: IdentifiableConflictChange[];
	deleted: IdentifiableConflictChange[];
};

export type PluginState = {
	decorations: DecorationSet;
	conflicts: Conflicts | undefined;
	selected: ConflictChangeWithType | undefined;
	isNextEnabled: boolean;
	isPreviousEnabled: boolean;
	resolutionMode: ResolutionMode;
};

const getBlockNodeAtPos = (pos: number, tr: Transaction) => {
	const $pos = tr.doc.resolve(pos);
	const node = $pos?.node();
	return node && node.isBlock && !node.isTextblock ? node : null;
};

const createInsertedDecoration = (
	conflict: IdentifiableConflictChange,
	tr: Transaction,
	resolutionMode: ResolutionMode,
): Decoration => {
	const node = getBlockNodeAtPos(conflict.from + 1, tr);
	if (node) {
		return createBlockNodeConflictDecoration(node, conflict.from);
	}

	const styles: React.CSSProperties | Record<`--${string}`, string> = {
		textDecoration: 'underline',
		textDecorationStyle: 'dotted',
		textDecorationThickness: token('space.025'),
		textDecorationColor: token('color.border.accent.orange'),
	};

	if (resolutionMode === 'resolving') {
		styles.background = token('color.background.accent.orange.subtlest');
	}

	return Decoration.inline(conflict.from, conflict.to, {
		style: convertToInlineCss(styles),
	});
};

const createSelectedDecoration = (
	conflict: ConflictChangeWithType,
	tr: Transaction,
): Decoration | undefined => {
	if (conflict.type === 'deleted') {
		return createDeletedDecoration(conflict, tr, true);
	}

	const node = getBlockNodeAtPos(conflict.from + 1, tr);
	if (node) {
		return createBlockNodeConflictDecoration(node, conflict.from);
	}

	return Decoration.inline(conflict.from, conflict.to, {
		style: convertToInlineCss({
			background: token('color.background.accent.orange.subtlest.hovered'),
			textDecoration: 'underline',
			textDecorationStyle: 'dotted',
			textDecorationThickness: token('space.025'),
			textDecorationColor: token('color.border.accent.orange'),
		}),
	});
};

function filterUndefined<Value>(c: Value | undefined): c is Value {
	return c !== undefined;
}

const createDecorationsFromConflicts = (
	conflicts: Conflicts | undefined,
	selectedConflict: ConflictChangeWithType | undefined,
	tr: Transaction,
	resolutionMode: ResolutionMode,
): Decoration[] => {
	if (!conflicts) {
		return [];
	}
	if (selectedConflict) {
		const selectedDecoration = createSelectedDecoration(selectedConflict, tr);
		const conflictDecorations = [
			...conflicts.inserted
				.filter((c) => c.id !== selectedConflict.id)
				.map((c) => createInsertedDecoration(c, tr, resolutionMode)),
			...conflicts.deleted
				.filter((c) => c.id !== selectedConflict.id)
				.map((c) => createDeletedDecoration(c, tr))
				.filter(filterUndefined),
		];
		if (selectedDecoration) {
			return conflictDecorations.concat(selectedDecoration);
		}
		return conflictDecorations;
	} else {
		return [
			...conflicts.inserted.map((c) => createInsertedDecoration(c, tr, resolutionMode)),
			...conflicts.deleted.map((c) => createDeletedDecoration(c, tr)).filter(filterUndefined),
		];
	}
};

const mapConflict = (
	change: IdentifiableConflictChange,
	mapping: Mapping,
	type: 'deleted' | 'inserted',
) => {
	const from = mapping.map(change.from);
	const to = mapping.map(change.to);

	// We've collapsed the change and there's no visible marker to the user this conflict is no longer useful to us
	// These are "orphaned" conflicts similar to orphaned comments and for now we handle by resolving these since there
	// are no appropriate user actions.
	if (type === 'inserted' && from === to) {
		return undefined;
	}
	return {
		...change,
		from,
		to,
	};
};

const findSelectedConflict = (conflict: IdentifiableConflictChange, selection: Selection) =>
	(selection.from >= conflict.from && selection.from <= conflict.to) ||
	(selection.to >= conflict.from && selection.to <= conflict.to);

const getSelectedConflict = (
	conflicts: Conflicts | undefined,
	selection: Selection,
): ConflictChangeWithType | undefined => {
	if (!conflicts) {
		return undefined;
	}
	const insertedSelectedConflict = conflicts?.inserted.find((conflict) =>
		findSelectedConflict(conflict, selection),
	);
	const deletedSelectedConflict = conflicts?.deleted.find((conflict) =>
		findSelectedConflict(conflict, selection),
	);
	if (insertedSelectedConflict) {
		return {
			...insertedSelectedConflict,
			type: 'inserted',
		};
	}

	if (deletedSelectedConflict) {
		return {
			...deletedSelectedConflict,
			type: 'deleted',
		};
	}
	return undefined;
};

export const createPlugin = (): SafePlugin => {
	return new SafePlugin({
		key: conflictResolutionPluginKey,
		state: {
			init: () => {
				return {
					decorations: DecorationSet.empty,
					conflicts: undefined,
					selected: undefined,
					isNextEnabled: false,
					isPreviousEnabled: false,
					resolutionMode: 'viewing',
				};
			},
			apply: (tr: Transaction, pluginState: PluginState) => {
				const meta: Metadata | undefined = tr.getMeta(conflictResolutionPluginKey);
				const navigatedConflict: ConflictChangeWithType | undefined = tr.getMeta('nextConflict');

				// Early escape hatch to avoid running all the code below if not necessary
				if (pluginState.conflicts === undefined && meta === undefined) {
					return pluginState;
				}

				const conflicts = getNewConflicts(meta, pluginState.conflicts);

				const mappedConflicts = pluginState.conflicts
					? {
							inserted: pluginState.conflicts.inserted
								.map((c) => mapConflict(c, tr.mapping, 'inserted'))
								.filter(filterUndefined),
							deleted: pluginState.conflicts.deleted
								.map((c) => mapConflict(c, tr.mapping, 'deleted'))
								.filter(filterUndefined),
						}
					: undefined;

				const conflictsRemaining = conflicts ?? mappedConflicts;

				// Update resolution mode based on metadata
				let resolutionMode = pluginState.resolutionMode;

				if (meta?.type === 'startResolving') {
					resolutionMode = 'resolving';
				} else if (meta?.type === 'stopResolving') {
					resolutionMode = 'viewing';
				} else if (conflicts && !pluginState.conflicts) {
					// Reset to viewing mode when new conflicts arrive
					resolutionMode = 'viewing';
				}

				// Only select conflicts if explicitly navigated to OR if actively resolving with cursor selection
				let selectedConflict: ConflictChangeWithType | undefined;

				if (navigatedConflict) {
					selectedConflict = navigatedConflict;
				} else if (resolutionMode === 'resolving') {
					selectedConflict = getSelectedConflict(conflictsRemaining, tr.selection);
				}

				const decorations =
					conflicts || selectedConflict || pluginState.selected
						? DecorationSet.create(
								tr.doc,
								createDecorationsFromConflicts(
									conflictsRemaining,
									selectedConflict,
									tr,
									resolutionMode,
								),
							)
						: pluginState.decorations.map(tr.mapping, tr.doc);

				return {
					decorations,
					conflicts: conflictsRemaining,
					selected: selectedConflict,
					isPreviousEnabled:
						conflictsRemaining && selectedConflict
							? getNavigationInfo(conflictsRemaining, selectedConflict, 'previous').isAbleToNavigate
							: false,
					isNextEnabled:
						conflictsRemaining && selectedConflict
							? getNavigationInfo(conflictsRemaining, selectedConflict, 'next').isAbleToNavigate
							: false,
					resolutionMode,
				};
			},
		},
		props: {
			decorations: (state: EditorState) => conflictResolutionPluginKey.getState(state)?.decorations,
		},
	});
};
